import axios from "axios";
import apiUrl from "../shared/config";

/**
 * Lock responses used for handling lock states.
 * @type {Object}
 * @property {string} LOCK_OWNED - Indicates that the lock is already owned by the user.
 * @property {string} LOCK_CREATED - Indicates that the lock was successfully created for the user.
 * @property {string} LOCK_EXISTS - Indicates that the lock already exists, and you would have to override the lock to submit changes.
 * @property {string} UNSEEN_CHANGES - Indicates that the content may have changed since your lock expired (if your lock expires but the pattern has not been updated it will let you submit a change).
 * @property {string} LOCK_REQUEST_FAILED - Indicates a failure, see console for explanation of error
 * @property {string} LOCK_REMOVED - You lock has successfully been removed from the locks table
 */
export const LockResponses = {
    LOCK_OWNED: 'LOCK_OWNED',
    LOCK_CREATED: 'LOCK_CREATED',
    LOCK_EXISTS: 'LOCK_EXISTS',
    UNSEEN_CHANGES: 'UNSEEN_CHANGES',
    LOCK_REQUEST_FAILED: 'LOCK_REQUEST_FAILED',
    LOCK_REMOVED: 'LOCK_REMOVED'
}

/**
 *
 * @type {object}
 * @param {string} PATTERN - A lock for a whole Pattern (currently used for the pattern edit view)
 * @param {string} EXAMPLE_GAME - A lock for an Example Game
 * @param {string} DESIGN_PROBLEM - A lock for a Design Problem
 * @param {string} PATTERN_DESCRIPTION - A lock for the Pattern Description
 */
export const LockType = {
    PATTERN: 'PATTERN',
    EXAMPLE_GAME: 'EXAMPLE_GAME',
    DESIGN_PROBLEM: 'DESIGN_PROBLEM',
    PATTERN_DESCRIPTION: 'PATTERN_DESCRIPTION',
}

function validResponse(response) {
    return response && response.data && response.data.status
        && Object.values(LockResponses).includes(response.data.status);
}


/**
 * Requests an edit lock.
 *
 * @param {string} token - Authentication token for the API request.
 * @param {string} primaryId - The primary ID of the item to lock.
 * @param {string} lockType - The lock type being requested.
 * @param {string|null} [elementId=null] - The optional element ID.
 * @return {Promise<string>} - A promise resolving to a LockResponse status.
 */
export async function getEditLock(token, primaryId, lockType, elementId = null) {
    if (!token) {
        console.error('No token provided to getEditLock');
        return LockResponses.LOCK_REQUEST_FAILED;
    }

    try {
        const response = await axios.get(
            `${apiUrl}Lock/requestEdit?token=${token}&primary_id=${primaryId}${
                elementId ? `&element_id=${elementId}` : ''
            }&lock_type=${lockType}`
        );

        // Ensure response structure is as expected. Don't think is should happen but I guess it "could"
        if (validResponse(response)) {
            return response.data.status;
        } else {
            console.error("Unexpected response structure", response);
            return LockResponses.LOCK_REQUEST_FAILED;
        }
    } catch (error) {
        console.error("Error retrieving edit lock:", error?.message);
        return LockResponses.LOCK_REQUEST_FAILED;
    }
}

/**
 * Overrides the specified lock
 *
 * @param {string} token - Authentication token for the API request.
 * @param {string} primaryId - The primary ID of the item to lock.
 * @param {string} lockType - The lock type being requested.
 * @param {string|null} [elementId=null] - The optional element ID.
 * @return {Promise<string>} - A promise resolving to a LockResponse status of LOCK_CREATED or LOCK_REQUEST_FAILED.
 */
export async function overrideEditLock(token, primaryId, lockType, elementId = null) {
    if (!token) {
        console.error('No token provided to overrideEditLock');
        return LockResponses.LOCK_REQUEST_FAILED;
    }

    try {
        const response = await axios.get(
            `${apiUrl}Lock/overrideLock?token=${token}&primary_id=${primaryId}${
                elementId ? `&element_id=${elementId}` : ''
            }&lock_type=${lockType}`
        );

        // Ensure response structure is as expected. Don't think is should happen but I guess it "could"
        if (validResponse(response) && response.data.status === LockResponses.LOCK_CREATED) {
            return LockResponses.LOCK_CREATED;
        } else {
            console.error("Error overriding lock:", response);
            return LockResponses.LOCK_REQUEST_FAILED;
        }
    } catch (error) {
        console.error("Error overriding lock:", error?.message);
        return LockResponses.LOCK_REQUEST_FAILED;
    }
}

/**
 * Removes the specified lock
 *
 * @param {string} token - Authentication token for the API request.
 * @param {string} primaryId - The primary ID of the item to lock.
 * @param {string} lockType - The lock type being requested.
 * @param {string|null} [elementId=null] - The optional element ID.
 * @return {Promise<string>} - A promise resolving to a LockResponse status of LOCK_CREATED or LOCK_REQUEST_FAILED.
 */
export async function cancelEditLock(token, primaryId, lockType, elementId = null) {
    if (!token) {
        console.error('No token provided to cancelEditLock');
        return LockResponses.LOCK_REQUEST_FAILED;
    }

    try {
        const response = await axios.get(
            `${apiUrl}Lock/cancelLock?token=${token}&primary_id=${primaryId}${
                elementId ? `&element_id=${elementId}` : ''
            }&lock_type=${lockType}`
        );

        // Ensure response structure is as expected. Don't think is should happen but I guess it "could"
        if (validResponse(response) && response.data.status === LockResponses.LOCK_REMOVED) {
            return LockResponses.LOCK_REMOVED;
        } else {
            console.error("Error canceling lock:", response);
            return LockResponses.LOCK_REQUEST_FAILED;
        }
    } catch (error) {
        console.error("Error canceling lock:", error?.message);
        return LockResponses.LOCK_REQUEST_FAILED;
    }
}

/**
 * Checks if your lock is still valid. If it is still valid, it returns LOCK_OWNED the specified lock.
 * If it is not valid, it checks if the pattern has been edited since the last time you think it was edited.
 * If it hasn't been edited it will return LOCK_CREATED. If it has been edited it will return LOCK_REQUEST_FAILED.
 *
 * @param {string} token - Authentication token for the API request.
 * @param {number} primaryId - The primary ID of the item to lock.
 * @param {string} lockType - The lock type being requested.
 * @param {string} editedDate - The timestamp of your sense of the last edit to the pattern.
 * @param {number|null} [elementId=null] - The optional element ID.
 * @return {Promise<string>} - A promise resolving to a LockResponse status of LOCK_CREATED or LOCK_REQUEST_FAILED.
 */
export async function requestLockUpdate(token, primaryId, lockType, editedDate, elementId = null) {
    if (!token) {
        console.error('No token provided to requestLockUpdate');
        return LockResponses.LOCK_REQUEST_FAILED;
    }

    try {
        const response = await axios.get(
            `${apiUrl}Lock/requestUpdate?token=${token}&primary_id=${primaryId}${
                elementId ? `&element_id=${elementId}` : ''
            }&lock_type=${lockType}&pattern_edited_date=${editedDate}`
        );

        // Ensure response structure is as expected. Don't think is should happen but I guess it "could"
        if (validResponse(response) && response.data.status ) {
            return response.data.status;
        } else {
            console.error("Error updating lock:", response);
            return LockResponses.LOCK_REQUEST_FAILED;
        }
    } catch (error) {
        console.error("Error updating lock:", error?.message);
        return LockResponses.LOCK_REQUEST_FAILED;
    }
}